import type { CatalogueCapability, CatalogueTechnology, CatalogueUsage, Capability, PqcStatus, UnmatchedDetection } from '@/api/types/catalogue'
import type { CbomAsset, MigrationFacts, MigrationItem } from '@/api/types/migration'
import type { MigrationScope } from '@/lib/migration/scope'

// ── Catalogue engine (I3 §9–§12) ────────────────────────────────────────────
// Capability-level status is the unit of truth; the roll-up is a view
// (Note 4). Gating joins usage → asset → migration item → step and compares
// the item's capability against the capability status. Computed, never stored.

const DAY = 86_400_000

export const STATUS_LABEL: Record<PqcStatus, string> = {
  pqc_ready: 'PQC-ready',
  partial: 'Partial',
  roadmap: 'Not PQC · roadmap',
  none: 'Not PQC · no roadmap',
  unknown: 'Unknown',
}
export const STATUS_SHORT: Record<PqcStatus, string> = { pqc_ready: 'PQC-ready', partial: 'partial', roadmap: 'roadmap', none: 'none', unknown: 'unknown' }
export const STATUS_TOKEN: Record<PqcStatus, string> = {
  pqc_ready: 'var(--rd-status-ok)',
  partial: 'var(--rd-status-info)',
  roadmap: 'var(--rd-status-warn)',
  none: 'var(--rd-status-neutral)',
  unknown: 'var(--rd-border-control)',
}
// Sort order: what needs attention first.
export const STATUS_RANK: Record<PqcStatus, number> = { roadmap: 0, none: 1, partial: 2, unknown: 3, pqc_ready: 4 }

export const capsOf = (techId: string, caps: CatalogueCapability[]) => caps.filter((c) => c.technology_id === techId)

// Roll-up (§9.3): ready if all relevant capabilities are; partial if some;
// roadmap if none is ready and any has a date; none if none and no date;
// unknown if unassessed.
export function rollup(caps: CatalogueCapability[], relevant?: Capability[]): PqcStatus {
  const xs = relevant?.length ? caps.filter((c) => relevant.includes(c.capability)) : caps
  if (!xs.length || xs.every((c) => c.status === 'unknown')) return 'unknown'
  const assessed = xs.filter((c) => c.status !== 'unknown')
  if (assessed.every((c) => c.status === 'pqc_ready')) return 'pqc_ready'
  if (assessed.some((c) => c.status === 'pqc_ready' || c.status === 'partial')) return 'partial'
  if (assessed.some((c) => c.roadmap_date)) return 'roadmap'
  return 'none'
}

// Earliest roadmap date on a technology (month), for the row and the chip.
export function roadmapOf(caps: CatalogueCapability[]): CatalogueCapability | null {
  return caps.filter((c) => c.roadmap_date && c.status !== 'pqc_ready').sort((a, b) => (a.roadmap_date ?? '').localeCompare(b.roadmap_date ?? ''))[0] ?? null
}

export const monthLabel = (ym: string | null | undefined) => {
  if (!ym) return '—'
  const [y, m] = ym.split('-').map(Number)
  return new Date(Date.UTC(y, (m || 1) - 1, 1)).toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' })
}
// End of the roadmap month, ISO date.
export const monthEnd = (ym: string) => {
  const [y, m] = ym.split('-').map(Number)
  return new Date(Date.UTC(y, m, 0, 12)).toISOString()
}
export const plusDays = (iso: string, days: number) => new Date(new Date(iso).getTime() + days * DAY).toISOString()

// ── Usage from Discovery (§9.3) ─────────────────────────────────────────────

export function matchTechnology(asset: Pick<CbomAsset, 'name' | 'detected_technology'>, techs: CatalogueTechnology[], mappings: Record<string, string> = {}): CatalogueTechnology | null {
  const name = (asset.detected_technology ?? asset.name).toLowerCase().trim()
  if (mappings[name]) return techs.find((t) => t.id === mappings[name]) ?? null
  return techs.find((t) => t.match_names.some((m) => m === name) || t.name.toLowerCase() === name) ?? null
}

export function usageFrom(orgId: string, facts: MigrationFacts | null, techs: CatalogueTechnology[], mappings: Record<string, string> = {}): { usage: CatalogueUsage[]; unmatched: UnmatchedDetection[] } {
  const usage: CatalogueUsage[] = []
  const unmatched = new Map<string, UnmatchedDetection>()
  const lastSeen = new Map((facts?.systems ?? []).map((s) => [s.id, s.last_scan_at]))
  for (const a of facts?.assets ?? []) {
    const t = matchTechnology(a, techs, mappings)
    if (t) {
      usage.push({ technology_id: t.id, org_id: orgId, asset_id: a.id, asset_code: a.code, version_detected: a.model_version, component_id: a.component_id ?? null, system_id: a.system_id, last_seen_at: lastSeen.get(a.system_id) ?? facts?.fetched_at ?? null })
    } else {
      const key = a.detected_technology ?? a.name
      const u = unmatched.get(key) ?? { name: key, asset_ids: [], system_ids: [] }
      u.asset_ids.push(a.id)
      if (!u.system_ids.includes(a.system_id)) u.system_ids.push(a.system_id)
      unmatched.set(key, u)
    }
  }
  return { usage, unmatched: Array.from(unmatched.values()) }
}

// ── Monograms (Note 8): deterministic colour pair per vendor ────────────────

const PALETTE: Record<'dark' | 'light', Array<[string, string]>> = {
  dark: [['#1e3a5f', '#bfdbfe'], ['#3b1d4f', '#e9d5ff'], ['#14352a', '#bbf7d0'], ['#43260f', '#fed7aa'], ['#3f1d24', '#fecdd3'], ['#123540', '#a5f3fc'], ['#33301a', '#fef08a'], ['#2a2f3a', '#e2e8f0']],
  light: [['#dbeafe', '#1e3a8a'], ['#f3e8ff', '#581c87'], ['#dcfce7', '#14532d'], ['#ffedd5', '#7c2d12'], ['#ffe4e6', '#881337'], ['#cffafe', '#164e63'], ['#fef9c3', '#713f12'], ['#e2e8f0', '#1e293b']],
}

export function monogram(vendor: string, theme: 'dark' | 'light'): { letters: string; bg: string; fg: string } {
  let h = 0
  for (let i = 0; i < vendor.length; i++) h = (h * 31 + vendor.charCodeAt(i)) >>> 0
  const [bg, fg] = PALETTE[theme][h % PALETTE[theme].length]
  const words = vendor.replace(/[()]/g, ' ').split(/\s+/).filter((w) => /[A-Za-z0-9]/.test(w))
  let letters: string
  if (words.length >= 2) letters = words.slice(0, 3).map((w) => w[0]).join('')
  else {
    const w = words[0] ?? '?'
    const consonants = w.replace(/[aeiou]/gi, '')
    letters = (w[0] + (consonants.slice(1, 2) || w[1] || '')).slice(0, 2)
  }
  return { letters: letters.toUpperCase(), bg, fg }
}

// ── Gating (§12.2 detection rule) ───────────────────────────────────────────

export interface Dependency {
  technology: CatalogueTechnology
  capability: Capability
  record: CatalogueCapability | null
  status: PqcStatus // capability status (or the technology roll-up when no record)
  gated: boolean
  roadmap_date: string | null
  asset: CbomAsset | null
  item: MigrationItem
}

export function itemDependencies(item: MigrationItem, scope: MigrationScope, usage: CatalogueUsage[], techs: CatalogueTechnology[], caps: CatalogueCapability[]): Dependency[] {
  const assetIds = scope.item_assets.filter((x) => x.item_id === item.id).map((x) => x.asset_id)
  const out: Dependency[] = []
  const seen = new Set<string>()
  for (const id of assetIds) {
    const u = usage.find((x) => x.asset_id === id)
    if (!u || seen.has(u.technology_id)) continue
    seen.add(u.technology_id)
    const technology = techs.find((t) => t.id === u.technology_id)
    if (!technology) continue
    const tcaps = capsOf(technology.id, caps)
    const cap = item.capability ?? tcaps[0]?.capability ?? null
    if (!cap) continue
    const record = tcaps.find((c) => c.capability === cap) ?? null
    const roll = rollup(tcaps)
    // none / roadmap on the capability; or a partial technology missing the capability the item needs.
    const status: PqcStatus = record ? record.status : roll === 'partial' ? 'none' : roll
    const gated = item.status !== 'closed' && (status === 'none' || status === 'roadmap')
    out.push({ technology, capability: cap, record, status, gated, roadmap_date: record?.roadmap_date ?? null, asset: scope.facts?.assets.find((a) => a.id === id) ?? null, item })
  }
  return out
}

export interface StepState {
  objective_code: string
  transition: string
  bullet_index: number
  items: MigrationItem[]
  dependencies: Dependency[]
  gated: Dependency[]
  state: 'vendor_gated' | 'dependencies_ready' | 'none'
  vendor_date: string | null // latest roadmap month among gated deps
}

export function stepState(code: string, transition: string, bullet: number, scope: MigrationScope | null, usage: CatalogueUsage[], techs: CatalogueTechnology[], caps: CatalogueCapability[]): StepState {
  const items = (scope?.items ?? []).filter((i) => i.step_links?.some((l) => l.objective_code === code && l.transition === transition && l.bullet_index === bullet))
  const dependencies = scope ? items.flatMap((i) => itemDependencies(i, scope, usage, techs, caps)) : []
  const gated = dependencies.filter((d) => d.gated)
  const vendor_date = gated.map((d) => d.roadmap_date).filter((x): x is string => !!x).sort().pop() ?? null
  return { objective_code: code, transition, bullet_index: bullet, items, dependencies, gated, state: gated.length ? 'vendor_gated' : dependencies.length ? 'dependencies_ready' : 'none', vendor_date }
}

// Objectives whose scope is the cryptographic estate (External dependencies
// card, dependency_changed fan-out): inventory, technology and vendor objectives.
const ESTATE_OBJECTIVES = ['RA-1', 'TE-1', 'TE-2', 'EE-1', 'EE-2']
export const objectiveHasEstateScope = (code: string) => ESTATE_OBJECTIVES.includes(code)

export function objectiveScopeAssets(code: string, facts: MigrationFacts | null): string[] {
  if (!objectiveHasEstateScope(code) || !facts) return []
  const cii = new Set(facts.systems.filter((s) => s.cii).map((s) => s.id))
  return facts.assets.filter((a) => cii.has(a.system_id)).map((a) => a.id)
}

// Assets an objective's External dependencies card reads (I3 §12.2, I4 §6.2):
// assets on components referenced by migration items linked to this
// objective's steps, or on systems in the objective's declared scope. Not the
// whole organisation.
export function objectiveDependencyAssets(code: string, scope: MigrationScope | null, declaredSystems: string[] = []): string[] {
  if (!scope?.facts) return []
  const components = new Set(scope.items.filter((i) => i.component_id && i.step_links?.some((l) => l.objective_code === code)).map((i) => i.component_id!))
  const systems = new Set(declaredSystems)
  return scope.facts.assets.filter((a) => (a.component_id && components.has(a.component_id)) || systems.has(a.system_id)).map((a) => a.id)
}

// Objectives affected by a technology: estate objectives whose scope includes
// an asset carrying it, plus objectives with a step linked to an item over it.
export function objectivesForTechnology(techId: string, scope: MigrationScope | null, usage: CatalogueUsage[]): string[] {
  const assets = new Set(usage.filter((u) => u.technology_id === techId).map((u) => u.asset_id))
  if (!assets.size || !scope) return []
  const out = new Set<string>()
  ESTATE_OBJECTIVES.forEach((code) => {
    if (objectiveScopeAssets(code, scope.facts).some((a) => assets.has(a))) out.add(code)
  })
  scope.items.forEach((i) => {
    if (scope.item_assets.some((x) => x.item_id === i.id && assets.has(x.asset_id))) i.step_links?.forEach((l) => out.add(l.objective_code))
  })
  return Array.from(out)
}

// Vendor gates for the projection (§12.2): the next level of an objective
// cannot be reached before its gated steps' vendor date + 30 days.
export function vendorGates(liveTransitions: Record<string, string>, scope: MigrationScope | null, usage: CatalogueUsage[], techs: CatalogueTechnology[], caps: CatalogueCapability[]): Record<string, string> {
  const gates: Record<string, string> = {}
  if (!scope) return gates
  scope.items.forEach((i) =>
    i.step_links?.forEach((l) => {
      if (liveTransitions[l.objective_code] !== l.transition) return
      const st = stepState(l.objective_code, l.transition, l.bullet_index, scope, usage, techs, caps)
      if (st.vendor_date) {
        const g = plusDays(monthEnd(st.vendor_date), 30)
        if (!gates[l.objective_code] || g > gates[l.objective_code]) gates[l.objective_code] = g
      }
    })
  )
  return gates
}
